import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import AdminSidebar from "../components/AdminSidebar";
import AdminAddGradeModal from "../components/AdminAddGradeModal.js";
import GradeFormatFields from "../components/GradeFormatFields.js";
import "../styles/AdminGrades.css";
import { encodePathSegment } from "../utils/apiPath";
import {
    gradePillClass,
    rowGradeToEditState,
    resolveRowGradeFormat,
    resolveGradeDisplay,
} from "../utils/gradeValues";
import { getErrorMessage, getFetchErrorMessage } from "../utils/errorUtils";
import ToastNotification from "../components/ToastNotification.js";

/**
 * AdminGradesPage (Admin -> Oceny)
 * - działa bez backendu (mock danych)
 * - layout zgodny z resztą: <AdminSidebar/> + <div className="main-content">
 */

// Removed mock data - will be fetched from API

function downloadTextFile(filename, content, mime = "text/plain") {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

function escapeCsv(v) {
    const s = String(v ?? "");
    if (/[",\n;]/.test(s)) return `"${s.replaceAll('"', '""')}"`;
    return s;
}

function buildCsv(rows) {
    return rows.map((r) => r.map(escapeCsv).join(";")).join("\n");
}

function getExamTypeLabel(examType) {
    if (!examType) return 'Egzamin';
    if (examType === 'FINAL') return 'Egzamin końcowy';
    if (examType === 'MIDTERM') return 'Egzamin śródsemestralny';
    if (examType === 'RESIT') return 'Egzamin poprawkowy';
    return examType;
}

export default function AdminGradesPage() {
    // State for API data
    const [groups, setGroups] = useState([]);
    const [modules, setModules] = useState([]);
    const [students, setStudents] = useState([]);
    const [grades, setGrades] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingGrades, setLoadingGrades] = useState(false);
    const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
    const notificationTimerRef = useRef(null);

    const showNotification = useCallback((message, type = "success") => {
        if (notificationTimerRef.current) {
            clearTimeout(notificationTimerRef.current);
        }
        const text =
            type === "error"
                ? getErrorMessage(message, "Wystąpił błąd. Spróbuj ponownie.")
                : message;
        setNotification({ show: true, message: text, type });
        notificationTimerRef.current = setTimeout(() => {
            setNotification({ show: false, message: "", type: "success" });
        }, 4000);
    }, []);

    useEffect(() => {
        return () => {
            if (notificationTimerRef.current) {
                clearTimeout(notificationTimerRef.current);
            }
        };
    }, []);

    const [groupId, setGroupId] = useState("");
    const [moduleId, setModuleId] = useState("");
    const [studentQuery, setStudentQuery] = useState("");
    const [editingGrade, setEditingGrade] = useState(null); // { studentId, gradeId, type, currentValue }
    const [editValue, setEditValue] = useState("");
    const [editFormat, setEditFormat] = useState("scale_5");
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [addModalStudentId, setAddModalStudentId] = useState("");
    const [addModalKind, setAddModalKind] = useState("final");

    const groupsForCourse = useMemo(() => groups, [groups]);
    const modulesForCourse = useMemo(() => modules, [modules]);
    const studentsForGroup = useMemo(() => students, [students]);

    // Fetch groups on mount (all groups)
    useEffect(() => {
        async function fetchGroups() {
            try {
                setLoading(true);
                const response = await fetch(`${process.env.REACT_APP_API_URL}/courses/getGroups`);
                if (!response.ok) throw new Error('Failed to fetch groups');
                const data = await response.json();
                const transformed = data.map(g => ({
                    id: g.group_code,
                    name: g.group_name
                }));
                setGroups(transformed);
            } catch (error) {
                console.error('Error fetching groups:', error);
                setGroups([]);
                showNotification(error, "error");
            } finally {
                setLoading(false);
            }
        }
        fetchGroups();
    }, [showNotification]);

    // Fetch modules when group changes
    useEffect(() => {
        async function fetchModules() {
            if (!groupId) {
                setModules([]);
                setModuleId("");
                return;
            }
            try {
                setLoading(true);
                const response = await fetch(`${process.env.REACT_APP_API_URL}/courses/groups/${encodePathSegment(groupId)}/modules`);
                if (!response.ok) throw new Error('Failed to fetch modules');
                const data = await response.json();
                const transformed = data.map(m => ({
                    id: m.module_id,
                    name: m.module_name,
                }));
                setModules(transformed);
                if (transformed.length > 0 && !moduleId) {
                    setModuleId(transformed[0].id.toString());
                } else if (transformed.length === 0) {
                    setModuleId("");
                }
            } catch (error) {
                console.error('Error fetching modules:', error);
                setModules([]);
                setModuleId("");
                showNotification(error, "error");
            } finally {
                setLoading(false);
            }
        }
        fetchModules();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupId, showNotification]);

    function openAddGradeModal(studentId = "", kind = "final") {
        setAddModalStudentId(studentId);
        setAddModalKind(kind);
        setAddModalOpen(true);
    }

    function openGradeEditor(row, editorMeta) {
        const { format, value } = rowGradeToEditState(row);
        setEditFormat(format);
        setEditValue(value);
        setEditingGrade(editorMeta);
    }

    function renderInlineGradeEditor({ onSave, onCancel }) {
        return (
            <div className="ag-inline-grade-editor">
                <GradeFormatFields
                    compact
                    className="ag-input ag-input--tiny"
                    format={editFormat}
                    value={editValue}
                    onFormatChange={(next) => {
                        setEditFormat(next);
                        if (next === "pass_fail" && !editValue) {
                            setEditValue("FAILED");
                        }
                    }}
                    onValueChange={setEditValue}
                />
                <button type="button" className="ag-btn ag-btn--tiny" onClick={onSave}>
                    OK
                </button>
                <button type="button" className="ag-btn ag-btn--ghost ag-btn--tiny" onClick={onCancel}>
                    ✕
                </button>
            </div>
        );
    }

    // Fetch students and grades when group and module are selected
    useEffect(() => {
        async function fetchStudentsAndGrades() {
            if (!groupId || !moduleId) {
                setStudents([]);
                setGrades({});
                return;
            }
            try {
                setLoadingGrades(true);
                // Fetch students
                const studentsResponse = await fetch(`${process.env.REACT_APP_API_URL}/courses/groups/${encodePathSegment(groupId)}/students`);
                if (!studentsResponse.ok) throw new Error('Failed to fetch students');
                const studentsData = await studentsResponse.json();
                const transformedStudents = studentsData.map(s => ({
                    id: s.student_id.toString(),
                    name: `${s.first_name} ${s.last_name}`,
                    email: s.email
                }));
                setStudents(transformedStudents);

                // Fetch all grades for the selected module (both exam and assignment grades)
                const gradesResponse = await fetch(`${process.env.REACT_APP_API_URL}/grades/admin/module/${moduleId}/group/${encodePathSegment(groupId)}`);
                if (!gradesResponse.ok) throw new Error('Failed to fetch grades');
                const gradesData = await gradesResponse.json();
                
                // Transform grades - store all grades per student (both exam and assignments)
                // Format: grades[groupId][moduleId][studentId] = array of grades
                // Initialize grades map for all students first
                const gradesMap = {};
                if (!gradesMap[groupId]) gradesMap[groupId] = {};
                if (!gradesMap[groupId][moduleId]) gradesMap[groupId][moduleId] = {};
                
                // Initialize empty arrays for all students
                transformedStudents.forEach(st => {
                    gradesMap[groupId][moduleId][st.id] = [];
                });
                
                // Add grades from API response
                // Process all students from the API response to ensure they're all included
                const processedStudentIds = new Set();
                gradesData.forEach(g => {
                    if (g.student_id && g.module_id) {
                        const studentIdStr = g.student_id.toString();
                        processedStudentIds.add(studentIdStr);
                        if (!gradesMap[groupId][moduleId][studentIdStr]) {
                            gradesMap[groupId][moduleId][studentIdStr] = [];
                        }
                        // Add all grade types with their details
                        if (g.grade !== null && g.grade !== undefined && g.grade_type) {
                            gradesMap[groupId][moduleId][studentIdStr].push({
                                id: g.id,
                                student_id: parseInt(studentIdStr, 10),
                                grade: g.grade,
                                grade_display: g.grade_display,
                                grade_format: g.grade_format,
                                is_passed: g.is_passed,
                                type: g.grade_type,
                                exam_type: g.exam_type,
                                exam_status: g.exam_status,
                                attempt: g.attempt,
                                assignment_title: g.assignment_title,
                                activity_type: g.activity_type,
                                date: g.exam_date || g.date || "",
                                lecturer_name: g.lecturer_name
                            });
                        }
                    }
                });
                
                // Ensure all students from transformedStudents have an entry (even if no grades)
                transformedStudents.forEach(st => {
                    if (!gradesMap[groupId][moduleId][st.id]) {
                        gradesMap[groupId][moduleId][st.id] = [];
                    }
                });
                
                setGrades(prev => ({
                    ...prev,
                    ...gradesMap
                }));
            } catch (error) {
                console.error('Error fetching students/grades:', error);
                showNotification(error, "error");
            } finally {
                setLoadingGrades(false);
            }
        }
        fetchStudentsAndGrades();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupId, moduleId, showNotification]);

    const visibleStudents = useMemo(() => {
        const q = studentQuery.trim().toLowerCase();
        if (!q) return studentsForGroup;
        return studentsForGroup.filter(
            (s) => s.name.toLowerCase().includes(q) || s.email.toLowerCase().includes(q)
        );
    }, [studentsForGroup, studentQuery]);

    // Get grades for current group and module
    const currentModuleGrades = useMemo(() => {
        if (!groupId || !moduleId) return {};
        return grades?.[groupId]?.[moduleId] || {};
    }, [grades, groupId, moduleId]);

    // Function to refresh grades
    const refreshGrades = async () => {
        if (!groupId || !moduleId) return;
        try {
            setLoadingGrades(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/grades/admin/module/${moduleId}/group/${encodePathSegment(groupId)}`);
            if (!response.ok) throw new Error('Failed to fetch grades');
            const data = await response.json();
            
            const gradesMap = {};
            if (!gradesMap[groupId]) gradesMap[groupId] = {};
            if (!gradesMap[groupId][moduleId]) gradesMap[groupId][moduleId] = {};
            
            // Initialize empty arrays for all students first
            students.forEach(st => {
                gradesMap[groupId][moduleId][st.id] = [];
            });
            
            // Add grades from API response
            data.forEach(g => {
                if (g.student_id && g.module_id) {
                    const studentIdStr = g.student_id.toString();
                    if (!gradesMap[groupId][moduleId][studentIdStr]) {
                        gradesMap[groupId][moduleId][studentIdStr] = [];
                    }
                    // Add all grade types with their details
                    if (g.grade_type && (g.grade !== null && g.grade !== undefined)) {
                        gradesMap[groupId][moduleId][studentIdStr].push({
                            id: g.id,
                            student_id: parseInt(studentIdStr, 10),
                            grade: g.grade,
                            grade_display: g.grade_display,
                            grade_format: g.grade_format,
                            is_passed: g.is_passed,
                            type: g.grade_type,
                            exam_type: g.exam_type,
                            exam_status: g.exam_status,
                            attempt: g.attempt,
                            assignment_title: g.assignment_title,
                            activity_type: g.activity_type,
                            date: g.exam_date || g.date || "",
                            lecturer_name: g.lecturer_name
                        });
                    }
                }
            });
            
            setGrades(prev => ({
                ...prev,
                ...gradesMap
            }));
            
            // Reset editing state after refresh
            setEditingGrade(null);
            setEditValue("");
        } catch (error) {
            console.error('Error refreshing grades:', error);
            showNotification(error, "error");
        } finally {
            setLoadingGrades(false);
        }
    };

    // Calculate stats for selected module
    function showGrade(grade) {
        if (!grade) return "—";
        return grade.grade_display ?? resolveGradeDisplay(grade);
    }

    const stats = useMemo(() => {
        const allValues = [];
        Object.values(currentModuleGrades || {}).forEach(studentGrades => {
            const finalGrade = studentGrades?.find((g) => g.type === "Ocena końcowa");
            if (finalGrade?.grade != null && finalGrade.grade !== "") {
                allValues.push(finalGrade);
            }
        });
        
        const numeric = allValues
            .filter((g) => {
                const fmt = resolveRowGradeFormat(g);
                return fmt === "scale_5" || fmt === "percent";
            })
            .map((g) => Number(g.grade))
            .filter((n) => !Number.isNaN(n));
        numeric.sort((a, b) => a - b);

        const avg = numeric.length ? numeric.reduce((a, b) => a + b, 0) / numeric.length : 0;
        const median = numeric.length
            ? numeric.length % 2 === 1
                ? numeric[(numeric.length - 1) / 2]
                : (numeric[numeric.length / 2 - 1] + numeric[numeric.length / 2]) / 2
            : 0;

        // Only count explicit "NP" and "ZW" values, not empty/missing grades
        const npCount = allValues.filter((g) => String(g.grade).toUpperCase() === "NP").length;
        const zwCount = allValues.filter((g) => String(g.grade).toUpperCase() === "ZW").length;

        // Calculate students without grades: total students minus students with grades
        // Note: students without grades are NOT counted as NP - NP is only for explicit "NP" grades
        const studentsWithGrades = Object.keys(currentModuleGrades || {}).length;
        const studentsWithoutGrades = Math.max(0, studentsForGroup.length - studentsWithGrades);
        
        return {
            count: allValues.length, // Only counts students with actual grades (not empty/missing)
            numericCount: numeric.length,
            avg: numeric.length ? avg : null,
            median: numeric.length ? median : null,
            npCount, // Only counts explicit "NP" grades, NOT students without grades
            zwCount,
            studentsWithoutGrades // Separate count for students without any grades
        };
    }, [currentModuleGrades, studentsForGroup.length]);

    const statsHint = "średnia z ocen końcowych liczbowych (skala i procenty)";

    async function handleUpdateGrade(gradeId, gradeType, value, gradeData) {
        if (!gradeType) return;
        if (gradeType !== 'Ocena końcowa' && !gradeId) return;
        if (gradeType === 'Ocena końcowa' && !gradeData?.student_id && !gradeId) return;
        
        try {
            const finalGradeId = gradeData.id || gradeId;
            
            // Determine the correct endpoint and format based on grade type
            let endpoint = '';
            let body = {};
            
            if (gradeType === 'Ocena końcowa') {
                // Update final grade using admin endpoint
                endpoint = `${process.env.REACT_APP_API_URL}/grades/admin/grade`;
                body = {
                    student_id: gradeData.student_id || (finalGradeId ? parseInt(finalGradeId.split('_')[1], 10) : null),
                    module_id: parseInt(moduleId, 10),
                    grade: (value && value !== "" && value !== "—") ? value : null,
                    grade_format: editFormat,
                };
            } else {
                // Use admin PUT endpoint for other grade types (no auth required for admin)
                // Verify gradeId format - should start with exam_, assignment_, or activity_
                if (!finalGradeId.match(/^(exam|assignment|activity)_/)) {
                    throw new Error(`Niepoprawny format ID oceny: ${finalGradeId}`);
                }
                
                endpoint = `${process.env.REACT_APP_API_URL}/grades/admin/${finalGradeId}`;
                body = {
                    grade: (value && value !== "" && value !== "—") ? value : null,
                    grade_format: editFormat,
                    gradeDate: gradeData.date || new Date().toISOString().split('T')[0]
                };
                
                // Add type-specific fields
                if (gradeType === 'Egzamin') {
                    body.examType = gradeData.exam_type;
                    body.examStatus = gradeData.exam_status;
                    if (editFormat === 'pass_fail' && value) {
                        body.examStatus = value === 'PASSED' ? 'PASSED' : 'FAILED';
                    }
                    body.attempt = gradeData.attempt;
                } else if (gradeType === 'Zadanie') {
                    body.feedback = gradeData.feedback || null;
                } else {
                    // Activity types (Odpowiedź ustna, Prezentacja, Quiz, etc.)
                    body.activityType = gradeData.activity_type;
                    body.weight = gradeData.weight;
                    body.description = gradeData.description;
                }
            }
            
            const response = await fetch(endpoint, {
                method: gradeType === 'Ocena końcowa' ? 'POST' : 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });
            
            if (!response.ok) {
                const msg = await getFetchErrorMessage(response, "Nie udało się zaktualizować oceny.");
                throw new Error(msg);
            }
            
            // Optimistically update local state immediately
            setGrades((prev) => {
                const next = { ...prev };
                if (!next[groupId]) next[groupId] = {};
                if (!next[groupId][moduleId]) next[groupId][moduleId] = {};
                
                const studentIdStr = gradeData.student_id?.toString() || finalGradeId.split('_')[1];
                const currentGrades = next[groupId][moduleId][studentIdStr] || [];
                
                // Find and update the grade in the array
                const gradeIndex = currentGrades.findIndex(g => g.id === finalGradeId);
                if (gradeIndex >= 0) {
                    const updatedGrades = [...currentGrades];
                    if (value && value !== "" && value !== "—") {
                        updatedGrades[gradeIndex] = {
                            ...updatedGrades[gradeIndex],
                            grade: value
                        };
                    } else {
                        // Remove grade if empty
                        updatedGrades.splice(gradeIndex, 1);
                    }
                    next[groupId][moduleId][studentIdStr] = updatedGrades;
                } else if (value && value !== "" && value !== "—") {
                    // Add new grade if it doesn't exist
                    next[groupId][moduleId][studentIdStr] = [
                        ...currentGrades,
                        {
                            id: finalGradeId,
                            student_id: parseInt(studentIdStr, 10),
                            grade: value,
                            type: gradeType,
                            exam_type: gradeData.exam_type,
                            exam_status: gradeData.exam_status,
                            attempt: gradeData.attempt,
                            assignment_title: gradeData.assignment_title,
                            activity_type: gradeData.activity_type,
                            date: gradeData.date || new Date().toISOString().split('T')[0],
                            lecturer_name: gradeData.lecturer_name
                        }
                    ];
                }
                
                return next;
            });
            
            // Reset editing state immediately
            setEditingGrade(null);
            setEditValue("");
            
            // Show success notification
            showNotification("Ocena została zaktualizowana pomyślnie", "success");

            refreshGrades().catch((err) => {
                console.error('Error refreshing grades after update:', err);
            });
        } catch (error) {
            console.error('Error updating grade:', error);
            showNotification(error, "error");
        }
    }

    function exportCsv() {
        const groupName = groupsForCourse.find((g) => g.id === groupId)?.name || "Grupa";
        const moduleName = modulesForCourse.find((m) => m.id === moduleId)?.name || "Przedmiot";

        const rows = [];
        rows.push(["Grupa", groupName]);
        rows.push(["Przedmiot", moduleName]);
        rows.push([]);
        rows.push(["Imię i nazwisko", "Email", "Ocena końcowa", "Egzamin", "Inne aktywności"]);

        (studentsForGroup || []).forEach((st) => {
            const studentGrades = currentModuleGrades?.[st.id] || [];
            
            // Ocena końcowa
            const finalGradeEntry = studentGrades.find((g) => g.type === "Ocena końcowa");
            const finalGrade = finalGradeEntry ? showGrade(finalGradeEntry) : "";
            
            // Egzaminy - wszystkie próby
            const examGrades = studentGrades.filter(g => g.type === 'Egzamin');
            const examGroups = {};
            examGrades.forEach(exam => {
                const examType = exam.exam_type || 'FINAL';
                if (!examGroups[examType]) {
                    examGroups[examType] = [];
                }
                examGroups[examType].push(exam);
            });
            const examDisplay = Object.keys(examGroups).map(type => {
                const exams = examGroups[type];
                exams.sort((a, b) => (a.attempt || 1) - (b.attempt || 1));
                const latestAttempt = Math.max(...exams.map(e => e.attempt || 1));
                return exams.map(e => {
                    const isLatest = (e.attempt || 1) === latestAttempt;
                    const label = showGrade(e);
                    return isLatest ? label : `~~${label}~~`;
                }).join(" ");
            }).join(" | ") || "";
            
            // Inne aktywności (zadania, aktywności)
            const otherGrades = studentGrades.filter(g => 
                g.type === 'Zadanie' || 
                (g.type && !g.type.includes('Egzamin') && g.type !== 'Ocena końcowa')
            );
            const otherDisplay =
                otherGrades.map((g) => `${g.type}: ${showGrade(g)}`).join(", ") || "";
            
            rows.push([
                st.name,
                st.email,
                finalGrade,
                examDisplay,
                otherDisplay,
            ]);
        });

        const csv = buildCsv(rows);
        const safe = `${groupName}_${moduleName}`.replaceAll(" ", "_");
        downloadTextFile(`oceny_${safe}.csv`, csv, "text/csv;charset=utf-8");
    }

    return (
        <div className="admin-grades-page">
            <AdminSidebar />
            
            <ToastNotification
                show={notification.show}
                message={notification.message}
                type={notification.type}
            />

            <div className="main-content">
                <div className="admin-grades-inner">
                    <header className="ag-header">
                        <div>
                            <h1 className="ag-title">Oceny</h1>
                            <p className="ag-subtitle">
                                Przeglądaj i edytuj oceny kursantów według kursu, grupy oraz modułu.
                            </p>
                        </div>

                        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                            <button
                                className="ag-btn ag-btn--primary"
                                type="button"
                                disabled={!groupId || !moduleId}
                                onClick={() => openAddGradeModal()}
                            >
                                Dodaj ocenę
                            </button>
                            <button className="ag-btn ag-btn--outline" type="button" onClick={exportCsv}>
                                Export CSV
                            </button>
                        </div>
                    </header>

                    <section className="ag-stats">
                        <div className="ag-statCard">
                            <div className="ag-statLabel">Wpisów</div>
                            <div className="ag-statValue">{stats.count}</div>
                            <div className="ag-statHint">w bieżącym module</div>
                        </div>

                        <div className="ag-statCard">
                            <div className="ag-statLabel">Średnia</div>
                            <div className="ag-statValue">{stats.avg === null ? "—" : stats.avg.toFixed(2)}</div>
                            <div className="ag-statHint">{statsHint}</div>
                            </div>

                        <div className="ag-statCard">
                            <div className="ag-statLabel">Mediana</div>
                            <div className="ag-statValue">{stats.median === null ? "—" : stats.median.toFixed(1)}</div>
                            <div className="ag-statHint">{statsHint}</div>
                        </div>

                        <div className="ag-statCard">
                            <div className="ag-statLabel">NP / ZW</div>
                            <div className="ag-statValue">
                                <span className="ag-red">{stats.npCount}</span> / <span className="ag-slate">{stats.zwCount}</span>
                            </div>
                            <div className="ag-statHint">tylko oceny NP/ZW</div>
                        </div>

                        {stats.studentsWithoutGrades > 0 && (
                            <div className="ag-statCard">
                                <div className="ag-statLabel">Bez ocen</div>
                                <div className="ag-statValue">{stats.studentsWithoutGrades}</div>
                                <div className="ag-statHint">kursantów bez ocen</div>
                            </div>
                        )}
                    </section>

                    <section className="ag-card">
                        <div className="ag-cardHeader">
                            <div className="ag-cardTitle">Edycja ocen</div>
                            <div className="ag-cardDesc">Wybierz grupę i przedmiot, aby zobaczyć wszystkie oceny kursantów (egzaminy i zadania).</div>
                        </div>

                        <div className="ag-filters">
                            <div className="ag-field">
                                <label className="ag-label">Grupa</label>
                                <select 
                                    className="ag-input" 
                                    value={groupId} 
                                    onChange={(e) => setGroupId(e.target.value)}
                                    disabled={loading}
                                >
                                    <option value="">Wybierz grupę...</option>
                                    {groupsForCourse.map((g) => (
                                        <option key={g.id} value={g.id}>{g.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="ag-field">
                                <label className="ag-label">Przedmiot</label>
                                <select 
                                    className="ag-input" 
                                    value={moduleId} 
                                    onChange={(e) => setModuleId(e.target.value)}
                                    disabled={loading || !groupId}
                                >
                                    <option value="">Wybierz przedmiot...</option>
                                    {modulesForCourse.map((m) => (
                                        <option key={m.id} value={m.id}>{m.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="ag-field ag-field--grow">
                                <label className="ag-label">Szukaj kursanta</label>
                                <input
                                    className="ag-input"
                                    value={studentQuery}
                                    onChange={(e) => setStudentQuery(e.target.value)}
                                    placeholder="Imię, nazwisko lub email..."
                                />
                            </div>
                        </div>

                        <div className="ag-grid">
                            <div className="ag-tableWrap" style={{ overflowX: 'auto' }}>
                                <table className="ag-table">
                                        <thead>
                                            <tr>
                                                <th>Kursant</th>
                                                <th>Email</th>
                                                <th>Ocena końcowa</th>
                                                <th>Egzamin</th>
                                                <th>Inne aktywności</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loadingGrades ? (
                                                <tr>
                                                    <td colSpan={5} className="ag-empty">Ładowanie danych...</td>
                                                </tr>
                                            ) : visibleStudents.length === 0 ? (
                                                <tr>
                                                    <td colSpan={5} className="ag-empty">Brak kursantów do wyświetlenia.</td>
                                                </tr>
                                            ) : (
                                                visibleStudents.map((st) => {
                                                    const studentGrades = currentModuleGrades?.[st.id] || [];
                                                    
                                                    // Grupuj oceny według typu
                                                    const finalGrade = studentGrades.find(g => g.type === 'Ocena końcowa');
                                                    const examGrades = studentGrades.filter(g => g.type === 'Egzamin');
                                                    const otherGrades = studentGrades.filter(g => 
                                                        g.type === 'Zadanie' || 
                                                        (g.type && !g.type.includes('Egzamin') && g.type !== 'Ocena końcowa')
                                                    );
                                                    
                                                    // Grupuj egzaminy według exam_type i wyświetl wszystkie próby
                                                    const examGroups = {};
                                                    examGrades.forEach(exam => {
                                                        const examType = exam.exam_type || 'FINAL';
                                                        if (!examGroups[examType]) {
                                                            examGroups[examType] = [];
                                                        }
                                                        examGroups[examType].push(exam);
                                                    });
                                                    
                                                    // Sortuj próby w każdej grupie
                                                    Object.keys(examGroups).forEach(type => {
                                                        examGroups[type].sort((a, b) => {
                                                            const attemptA = a.attempt || 1;
                                                            const attemptB = b.attempt || 1;
                                                            return attemptA - attemptB;
                                                        });
                                                    });
                                                    
                                                    return (
                                                        <tr key={st.id} className="ag-row">
                                                            <td className="ag-student">{st.name}</td>
                                                            <td className="ag-muted">{st.email}</td>
                                                            <td>
                                                                {editingGrade && editingGrade.studentId === st.id && editingGrade.type === 'final' ? (
                                                                    renderInlineGradeEditor({
                                                                        onSave: () => {
                                                                            handleUpdateGrade(
                                                                                finalGrade?.id,
                                                                                'Ocena końcowa',
                                                                                editValue,
                                                                                { ...(finalGrade || {}), student_id: parseInt(st.id, 10) }
                                                                            );
                                                                        },
                                                                        onCancel: () => {
                                                                            setEditingGrade(null);
                                                                            setEditValue("");
                                                                        },
                                                                    })
                                                                ) : finalGrade ? (
                                                                    <span 
                                                                        className={`ag-pill ag-pill--editable ${gradePillClass(finalGrade.grade, resolveRowGradeFormat(finalGrade))}`}
                                                                        onClick={() => {
                                                                            openGradeEditor(finalGrade, {
                                                                                studentId: st.id,
                                                                                gradeId: finalGrade.id,
                                                                                type: 'final',
                                                                            });
                                                                        }}
                                                                        title="Kliknij, aby edytować"
                                                                    >
                                                                        {showGrade(finalGrade)}
                                                                    </span>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        className="ag-muted"
                                                                        style={{ background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
                                                                        onClick={() => openAddGradeModal(st.id, "final")}
                                                                    >
                                                                        + Dodaj
                                                                    </button>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {Object.keys(examGroups).length > 0 ? (
                                                                    <div className="ag-exam-grades">
                                                                        {Object.keys(examGroups).map((examType, typeIdx, typeArray) => {
                                                                            const exams = examGroups[examType];
                                                                            const latestAttempt = Math.max(...exams.map(e => e.attempt || 1));
                                                                            const examTypeLabel = getExamTypeLabel(examType);
                                                                            
                                                                            return (
                                                                                <span key={examType} className="ag-exam-type-group">
                                                                                    <span className="ag-exam-type-label">{examTypeLabel}:</span>
                                                                                    <span className="ag-exam-attempts">
                                                                                        {exams.map((exam, idx) => {
                                                                                            const isLatest = (exam.attempt || 1) === latestAttempt;
                                                                                            const isEditing = editingGrade && editingGrade.studentId === st.id && editingGrade.gradeId === exam.id;
                                                                                            
                                                                                            return isEditing ? (
                                                                                                renderInlineGradeEditor({
                                                                                                    onSave: () => {
                                                                                                        handleUpdateGrade(exam.id, 'Egzamin', editValue, exam);
                                                                                                    },
                                                                                                    onCancel: () => {
                                                                                                        setEditingGrade(null);
                                                                                                        setEditValue("");
                                                                                                    },
                                                                                                })
                                                                                            ) : (
                                                                                                <span 
                                                                                                    key={idx}
                                                                                                    className={`ag-pill ag-pill--editable ${gradePillClass(exam.grade, resolveRowGradeFormat(exam))} ${isLatest ? '' : 'ag-exam-strikethrough'}`}
                                                                                                    onClick={() => {
                                                                                                        openGradeEditor(exam, {
                                                                                                            studentId: st.id,
                                                                                                            gradeId: exam.id,
                                                                                                            type: 'exam',
                                                                                                        });
                                                                                                    }}
                                                                                                    title="Kliknij, aby edytować"
                                                                                                >
                                                                                                    {showGrade(exam)}
                                                                                                </span>
                                                                                            );
                                                                                        })}
                                                                                    </span>
                                                                                    {typeIdx < typeArray.length - 1 && (
                                                                                        <span className="ag-exam-separator">|</span>
                                                                                    )}
                                                                                </span>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        className="ag-muted"
                                                                        style={{ background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
                                                                        onClick={() => openAddGradeModal(st.id, "exam")}
                                                                    >
                                                                        + Dodaj
                                                                    </button>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {otherGrades.length > 0 ? (
                                                                    <div className="ag-other-grades">
                                                                        {otherGrades.map((grade, idx) => {
                                                                            // Use grade.id directly (from backend) - it's already in correct format
                                                                            const gradeId = grade.id;
                                                                            if (!gradeId) {
                                                                                console.warn('Grade missing ID:', grade);
                                                                                return null;
                                                                            }
                                                                            const isEditing = editingGrade && editingGrade.studentId === st.id && editingGrade.gradeId === gradeId;
                                                                            
                                                                            return (
                                                                                <span key={idx} className="ag-other-grade-item">
                                                                                    {isEditing ? (
                                                                                        <>
                                                                                            {renderInlineGradeEditor({
                                                                                                onSave: () => {
                                                                                                    handleUpdateGrade(gradeId, grade.type, editValue, grade);
                                                                                                },
                                                                                                onCancel: () => {
                                                                                                    setEditingGrade(null);
                                                                                                    setEditValue("");
                                                                                                },
                                                                                            })}
                                                                                            <span className="ag-grade-type-label">
                                                                                                {grade.type}
                                                                                            </span>
                                                                                        </>
                                                                                    ) : (
                                                                                        <>
                                                                                            <span 
                                                                                                className={`ag-pill ag-pill--small ag-pill--editable ${gradePillClass(grade.grade, resolveRowGradeFormat(grade))}`}
                                                                                                onClick={() => {
                                                                                                    openGradeEditor(grade, {
                                                                                                        studentId: st.id,
                                                                                                        gradeId: gradeId,
                                                                                                        type: grade.type,
                                                                                                    });
                                                                                                }}
                                                                                                title="Kliknij, aby edytować"
                                                                                            >
                                                                                                {showGrade(grade)}
                                                                                            </span>
                                                                                            <span className="ag-grade-type-label">
                                                                                                {grade.type}
                                                                                            </span>
                                                                                        </>
                                                                                    )}
                                                                                </span>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        className="ag-muted"
                                                                        style={{ background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
                                                                        onClick={() => openAddGradeModal(st.id, "activity")}
                                                                    >
                                                                        + Dodaj
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                            </div>

                            <aside className="ag-side">
                                <div className="ag-sideHeader">
                                    <div className="ag-sideTitle">Podsumowanie</div>
                                    <div className="ag-sideDesc">
                                        Grupa: <b>{groupsForCourse.find((g) => g.id === groupId)?.name || "—"}</b><br />
                                        Przedmiot: <b>{modulesForCourse.find((m) => m.id === moduleId)?.name || "—"}</b>
                                    </div>
                                </div>

                                <div className="ag-sideBody">
                                    <div className="ag-kpi">
                                        <span className="ag-kpiKey">Oceny liczbowe</span>
                                        <span className="ag-kpiVal">{stats.numericCount}</span>
                                    </div>
                                    <div className="ag-kpi">
                                        <span className="ag-kpiKey">NP</span>
                                        <span className="ag-kpiVal ag-red">{stats.npCount}</span>
                                    </div>
                                    <div className="ag-kpi">
                                        <span className="ag-kpiKey">ZW</span>
                                        <span className="ag-kpiVal ag-slate">{stats.zwCount}</span>
                                    </div>

                                </div>
                            </aside>
                        </div>
                    </section>
                </div>
            </div>

            <AdminAddGradeModal
                isOpen={addModalOpen}
                onClose={() => setAddModalOpen(false)}
                onSuccess={() => {
                    refreshGrades();
                    showNotification("Ocena została dodana", "success");
                }}
                students={studentsForGroup}
                groupId={groupId}
                moduleId={moduleId}
                preselectedStudentId={addModalStudentId}
                preselectedKind={addModalKind}
            />
        </div>
    );
}

